import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const SERVICES = [
  {
    num: "01",
    id: "residential-window-cleaning-billings",
    title: "Residential",
    subtitle: "Window Cleaning",
    description:
      "Residential window cleaning in Billings, Montana — inside and out, sill to sash. We hand-detail every pane in your home, screens, tracks, and frames included, and lift off hard-water stains so the Big Sky pours in unobstructed.",
    tags: ["Interior & Exterior Window Washing", "Screen & Track Cleaning", "Hard-Water Stain Removal"],
    testId: "service-card-residential",
  },
  {
    num: "02",
    id: "commercial-window-cleaning-billings",
    title: "Commercial",
    subtitle: "Window Cleaning",
    description:
      "Commercial window cleaning for Billings storefronts, offices, and multi-story buildings. Scheduled window washing routes keep your business gleaming — because first impressions start at the window.",
    tags: ["Storefront Window Washing", "Scheduled Cleaning Routes", "High-Reach Glass"],
    testId: "service-card-commercial",
  },
  {
    num: "03",
    id: "gutter-cleaning-billings",
    title: "Gutter",
    subtitle: "Cleaning",
    description:
      "Gutter cleaning and gutter brightening for Billings homes. We clear out leaves, pine needles, and roof grit and flush downspouts, so meltwater and rain flow where they should — no wobbling ladders on your weekend.",
    tags: ["Gutter Debris Removal", "Downspout Flushing", "Gutter Brightening"],
    testId: "service-card-gutters",
  },
  {
    num: "04",
    id: "pressure-washing-billings",
    title: "Pressure Washing",
    subtitle: "& Extras",
    description:
      "Pressure washing in Billings, MT — siding, driveways, sidewalks, and concrete — plus the finishing touches that make a whole exterior look new again. One visit, top to bottom, above the foundation.",
    tags: ["House & Siding Washing", "Driveways & Concrete", "Exterior Add-Ons"],
    testId: "service-card-pressure-washing",
  },
];

const ServiceRow = ({ service, index }) => (
  <motion.article
    data-testid={service.testId}
    id={service.id}
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.9, delay: index * 0.1, ease: [0.16, 1, 0.3, 1] }}
    className="group grid grid-cols-1 items-start gap-8 border-b border-zinc-200 py-14 md:grid-cols-12 md:py-20"
  >
    <span
      className="font-display text-6xl font-black leading-none tracking-tighter text-zinc-200 transition-colors duration-500 group-hover:text-sky-500 md:col-span-2 md:text-7xl"
      aria-hidden="true"
    >
      {service.num}
    </span>
    <h3 className="font-display text-4xl font-bold leading-[0.95] tracking-tight text-zinc-950 md:col-span-4 md:text-5xl">
      {service.title}
      <br />
      <span className="text-sky-500">{service.subtitle}</span>
    </h3>
    <div className="md:col-span-5">
      <p className="max-w-lg text-sm leading-relaxed text-zinc-600 md:text-base">
        {service.description}
      </p>
      <div className="mt-6 flex flex-wrap gap-2">
        {service.tags.map((tag) => (
          <span
            key={tag}
            className="rounded-full border border-zinc-200 px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.15em] text-zinc-500 transition-colors duration-300 group-hover:border-sky-500/40"
          >
            {tag}
          </span>
        ))}
      </div>
    </div>
    <a
      href="tel:4066963771"
      data-testid={`${service.testId}-quote-link`}
      aria-label={`Get a quote for ${service.title} ${service.subtitle}`}
      className="flex h-14 w-14 shrink-0 items-center justify-center self-start rounded-full bg-zinc-950 text-white transition-all duration-300 hover:scale-110 hover:bg-sky-500 md:col-span-1 md:justify-self-end"
    >
      <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:rotate-45" />
    </a>
  </motion.article>
);

export const Services = () => {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="mx-auto max-w-[1400px] px-6 py-32 md:px-12 md:py-44"
    >
      <div className="mb-20 flex flex-col gap-6 md:mb-28 md:flex-row md:items-end md:justify-between">
        <Reveal>
          <p className="mb-4 text-xs font-bold uppercase tracking-[0.35em] text-sky-500">
            Window Cleaning Services in Billings, MT
          </p>
          <h2 className="max-w-xl font-display text-4xl font-black leading-[0.95] tracking-tighter text-zinc-950 sm:text-5xl lg:text-6xl">
            Four ways we make Billings look its best
          </h2>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="max-w-sm text-sm leading-relaxed text-zinc-500 md:text-base">
            No ladders wobbling against your gutters, no streaks at golden hour.
            Just pure, invisible glass — every single visit.
          </p>
        </Reveal>
      </div>
      <div className="border-t border-zinc-200">
        {SERVICES.map((service, i) => (
          <ServiceRow key={service.num} service={service} index={i} />
        ))}
      </div>
    </section>
  );
};
